import useIntervallo from '../hooks/useIntervallo';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSessions, createSessionAsync, deleteSessionAsync, restartSessionAsync, relinkSessionAsync, setSessionQR,
} from '../store/sessionSlice';
import { getSessionQR } from '../api/sessions';
import StatusBadge from '../components/StatusBadge';
import { Button, ConfirmDialog, EmptyState, PageHeader } from '../components/ui';
import QRCodeModal from '../components/QRCodeModal';
import { messaggioErrore } from '../utils/apiError';
import { MdAdd, MdDelete, MdLink, MdRefresh, MdWarningAmber } from 'react-icons/md';
import { toast } from 'react-toastify';

// Un account sano diventa operativo in pochi secondi; quello con più chat ci
// mette una quarantina di secondi. Oltre i tre minuti non sta più entrando:
// si è piantato, e continuare a dire che "sta preparando la connessione"
// manda la persona ad aspettare qualcosa che non arriverà.
export const ATTESA_MASSIMA_MS = 3 * 60 * 1000;
const IN_AVVIO = ['initializing', 'authenticated'];

export function diagnosi(session, adesso = Date.now()) {
  if (!IN_AVVIO.includes(session.status)) return null;
  const da = session.status_changed_at ? new Date(session.status_changed_at).getTime() : null;
  const fermaDa = da ? adesso - da : 0;
  if (!da || fermaDa < ATTESA_MASSIMA_MS) {
    return {
      bloccata: false,
      testo: session.status === 'authenticated'
        ? 'QR accettato: WhatsApp sta preparando la connessione.'
        : 'Avvio in corso.',
    };
  }
  const minuti = Math.round(fermaDa / 60000);
  return {
    bloccata: true,
    testo: `Ferma da ${minuti} minuti senza diventare operativa: la connessione si è piantata. `
      + 'Riavviala: non serve un nuovo QR.',
  };
}

export default function SessionsPage() {
  const dispatch = useDispatch();
  const { items: sessions, loading } = useSelector((state) => state.sessions);
  const [activeModal, setActiveModal] = useState(null);
  const [riavvioInCorso, setRiavvioInCorso] = useState(null);
  // Il tempo passa anche quando nessun dato cambia: senza questo, una sessione
  // ferma resterebbe "in preparazione" finché non arriva un altro sondaggio.
  const [adesso, setAdesso] = useState(() => Date.now());

  useEffect(() => {
    dispatch(fetchSessions());
    const orologio = setInterval(() => setAdesso(Date.now()), 10000);
    return () => clearInterval(orologio);
  }, [dispatch]);
  // Stato e QR arrivano anche dal socket; il sondaggio copre i passaggi che
  // il socket non annuncia, e si ferma a scheda nascosta.
  useIntervallo(() => dispatch(fetchSessions()), 5000);

  // Il QR arriva dal socket, ma WhatsApp tiene il primo per un minuto: se il
  // socket se lo perde la finestra resta vuota a lungo. Con la finestra aperta
  // lo si chiede anche al server, finché la connessione non è pronta.
  const inAttesaQR = sessions.find(s => s.id === activeModal && s.status !== 'ready');
  const chiediQR = async () => {
    if (!inAttesaQR) return;
    try {
      const { data } = await getSessionQR(inAttesaQR.id);
      if (data?.qrCode) dispatch(setSessionQR({ userId: inAttesaQR.session_id, qrCode: data.qrCode }));
    } catch (_) { /* Non ancora generato: ci pensa il prossimo giro o il socket. */ }
  };
  useEffect(() => { chiediQR(); }, [inAttesaQR?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useIntervallo(chiediQR, 4000, !!inAttesaQR);

  const handleCreate = async () => {
    try {
      const result = await dispatch(createSessionAsync()).unwrap();
      setActiveModal(result.id);
      toast.success('Sessione creata');
    } catch (motivo) {
      toast.error(typeof motivo === 'string' && motivo ? motivo : 'Collegamento non riuscito. Riprova.');
    }
  };

  // Eliminare scollega il telefono e cancella le credenziali dal server: non
  // si annulla, quindi si chiede conferma. Prima bastava un clic sull'icona.
  const [daEliminare, setDaEliminare] = useState(null);
  const [daRicollegare, setDaRicollegare] = useState(null);
  const handleDelete = async () => {
    const id = daEliminare?.id;
    setDaEliminare(null);
    await dispatch(deleteSessionAsync(id));
    toast.info('Account scollegato: sul telefono il dispositivo non compare più.');
  };
  const handleRelink = async () => {
    const id = daRicollegare?.id;
    setDaRicollegare(null);
    try {
      await dispatch(relinkSessionAsync(id)).unwrap();
      setActiveModal(id);
      toast.info('Inquadra il nuovo QR con lo stesso telefono di prima.');
    } catch (errore) {
      toast.error(messaggioErrore(errore, 'Non è stato possibile ricollegare.'));
    }
  };

  const handleRestart = async (id) => {
    setRiavvioInCorso(id);
    try {
      await dispatch(restartSessionAsync(id)).unwrap();
      toast.info('Connessione riavviata: torna operativa da sola in qualche decina di secondi.');
    } catch (errore) {
      toast.error(messaggioErrore(errore));
    } finally {
      setRiavvioInCorso(null);
    }
  };

  const modalSession = sessions.find((s) => s.id === activeModal);

  return (
    <div>
      <PageHeader title="Account WhatsApp"
        subtitle="Ogni account è un telefono collegato. Serve per inviare, ricevere e leggere la rubrica."
        actions={<Button variant="primary" icon={MdAdd} loading={loading} onClick={handleCreate}>
          Collega un account
        </Button>} />

      {sessions.length === 0 ? (
        <EmptyState icon={MdAdd} title="Nessun account collegato"
          hint="Collega WhatsApp scansionando il codice QR: serve il telefono a portata di mano." />
      ) : (
        <div className="grid gap-3">
          {sessions.map((session) => {
            const stato = diagnosi(session, adesso);
            return (
              <div key={session.id} className="bg-surface rounded-lg border border-line p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">
                      {session.phone_number || session.session_id}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      Creata: {new Date(session.created_at).toLocaleString('it-IT')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={session.status} />
                    <Button size="sm" icon={MdRefresh}
                      variant={stato?.bloccata ? 'primary' : 'secondary'}
                      loading={riavvioInCorso === session.id}
                      onClick={() => handleRestart(session.id)}>
                      Riavvia
                    </Button>
                    {session.status !== 'ready' && session.qr_code && (
                      <Button size="sm" onClick={() => setActiveModal(session.id)}>QR Code</Button>
                    )}
                    {session.status !== 'ready' && (
                      <Button size="sm" icon={MdLink} onClick={() => setDaRicollegare(session)}>Ricollega</Button>
                    )}
                    <button type="button" onClick={() => setDaEliminare(session)}
                      aria-label={`Disconnetti ${session.phone_number || session.session_id}`}
                      className="p-2 text-muted hover:text-danger transition-colors">
                      <MdDelete size={18} aria-hidden />
                    </button>
                  </div>
                </div>

                {session.status_detail && (
                  <p role="alert" className="mt-3 rounded-lg border border-warning/40 bg-warning-bg px-3 py-2 text-xs text-warning">
                    {session.status_detail}
                  </p>
                )}
                {stato && (
                  <p role={stato.bloccata ? 'alert' : undefined}
                    className={`mt-3 flex items-start gap-1.5 text-xs ${
                      stato.bloccata
                        ? 'rounded-lg border border-warning/40 bg-warning-bg px-3 py-2 text-warning'
                        : 'text-muted'}`}>
                    {stato.bloccata && <MdWarningAmber size={16} aria-hidden className="mt-px shrink-0" />}
                    {stato.testo}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeModal && modalSession && (
        <QRCodeModal
          qrCode={modalSession.qr_code}
          status={modalSession.status}
          onClose={() => setActiveModal(null)}
        />
      )}
      <ConfirmDialog open={!!daEliminare} onClose={() => setDaEliminare(null)} onConfirm={handleDelete} danger
        title="Scollegare questo account?" confirmLabel="Scollega ed elimina"
        message={`Il telefono ${daEliminare?.phone_number || ''} viene scollegato e le credenziali cancellate dal server. Le chat già salvate restano consultabili. Per usarlo di nuovo servirà un nuovo QR.`} />
      <ConfirmDialog open={!!daRicollegare} onClose={() => setDaRicollegare(null)} onConfirm={handleRelink}
        title="Ricollegare il telefono?" confirmLabel="Mostra il nuovo QR"
        message={`Il collegamento attuale viene chiuso e ne prepari uno nuovo sulla stessa connessione: chat, rubrica e regole restano dove sono. Usa lo stesso telefono${daRicollegare?.phone_number ? ` (${daRicollegare.phone_number})` : ''}: con un numero diverso la connessione si ferma.`} />
    </div>
  );
}
